import { useState } from 'react';
import Image from 'next/image';

import type { MarketingCopyAsset } from '@lib/sanity';

const DEFAULT_VIDEO_TITLE = 'Fantasy Football Hub video';
// The hero column's widest render, so the poster's srcset tops out there.
const HERO_MEDIA_SIZES = '(min-width: 1024px) 560px, 100vw';

const buildVideoSource = (url: string, startTime: number | undefined): string =>
  startTime !== undefined && startTime > 0 ? `${url}#t=${startTime}` : url;

const FRAME_CLASSES =
  'relative aspect-video w-full overflow-hidden rounded-2xl bg-neutral-800 shadow-500';

type HeroVideoProps = {
  asset: MarketingCopyAsset;
};

/**
 * The hero's media. A video shows its poster frame and only fetches the file
 * once the play button is pressed, so the page's largest paint is a small
 * image rather than a multi-megabyte video. The fixed 16:9 frame keeps the
 * layout still while either loads.
 */
const HeroVideo = ({ asset }: HeroVideoProps) => {
  const [isPlaying, setIsPlaying] = useState(false);

  if (asset.kind === 'image') {
    return (
      <div className={FRAME_CLASSES}>
        <Image
          src={asset.url}
          alt={asset.alt}
          fill
          priority
          fetchPriority="high"
          sizes={HERO_MEDIA_SIZES}
          className="object-cover"
        />
      </div>
    );
  }

  const title = asset.title ?? DEFAULT_VIDEO_TITLE;

  if (isPlaying) {
    return (
      <div className={FRAME_CLASSES}>
        <video
          className="absolute inset-0 h-full w-full object-cover"
          src={buildVideoSource(asset.url, asset.startTime)}
          poster={asset.posterUrl}
          title={title}
          controls
          autoPlay
          playsInline
        />
      </div>
    );
  }

  return (
    <div className={FRAME_CLASSES}>
      {asset.posterUrl && (
        <Image
          src={asset.posterUrl}
          alt=""
          fill
          priority
          fetchPriority="high"
          sizes={HERO_MEDIA_SIZES}
          className="object-cover"
        />
      )}
      <button
        type="button"
        onClick={() => setIsPlaying(true)}
        className="group absolute inset-0 flex items-center justify-center bg-gradient-to-t from-black/50 via-transparent to-transparent"
        data-cy="hero-video-play"
      >
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white/95 shadow-500 transition-transform duration-150 group-hover:scale-105 motion-reduce:transition-none">
          <span
            aria-hidden="true"
            className="ml-1 h-0 w-0 border-y-[11px] border-l-[18px] border-y-transparent border-l-landing-ink"
          />
        </span>
        <span className="sr-only">{`Play video: ${title}`}</span>
        <span
          aria-hidden="true"
          className="absolute bottom-4 left-4 font-open-sans text-sm font-semibold text-white"
        >
          {title}
        </span>
      </button>
    </div>
  );
};

export default HeroVideo;
